import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../lib/supabase';

const { FiX, FiCalendar, FiClock, FiUser, FiMail, FiPhone, FiMessageSquare, FiCheck, FiChevronLeft, FiChevronRight } = FiIcons;
const { FaCar } = FaIcons;

const CalendarScheduler = ({ isOpen, onClose, vehicleInfo, inquiryType = 'test-drive' }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date()); // Auto-select today's date
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const timeSlots = [
    '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM', '5:00 PM'
  ];

  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      loadAvailableSlots();
    }
  }, [isOpen, currentMonth]);

  const loadAvailableSlots = async () => {
    // In a real app, this would fetch from the database
    // For now, generate random availability
    const slots = {};
    const daysInMonth = getDaysInMonth(currentMonth);
    
    daysInMonth.forEach(date => {
      if (date >= new Date().setHours(0, 0, 0, 0)) {
        const dateKey = date.toISOString().split('T')[0];
        // Randomly mark some time slots as unavailable
        slots[dateKey] = timeSlots.filter(() => Math.random() > 0.3);
      }
    });
    
    setAvailableSlots(slots);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    for (let d = firstDay; d <= lastDay; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    
    return days;
  };

  const getDayOfWeek = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      alert('Please select a date and time');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create appointment in database
      const appointmentData = {
        type: inquiryType,
        vehicle_info: vehicleInfo,
        scheduled_date: selectedDate.toISOString().split('T')[0],
        scheduled_time: selectedTime,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        notes: formData.message,
        status: 'scheduled',
        created_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('service_appointments')
        .insert([appointmentData]);
      
      if (error) throw error;
      
      // Fire Google Ads conversion event
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17797421129/LrUYCIi_tdMbEMmwvKZC'
        });
        console.log('✅ Google Ads conversion event fired');
      }
      
      setSuccess(true);
      
      // Reset form after 2 seconds and close
      setTimeout(() => {
        setSelectedDate(null);
        setSelectedTime('');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setSuccess(false);
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      alert('Failed to schedule appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isDateAvailable = (date) => {
    const dateKey = date.toISOString().split('T')[0];
    return availableSlots[dateKey] && availableSlots[dateKey].length > 0;
  };

  const isDatePast = (date) => {
    return date < new Date().setHours(0, 0, 0, 0);
  };

  const getDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Schedule {inquiryType === 'test-drive' ? 'Test Drive' : 'Appointment'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                <SafeIcon icon={FaCar} className="inline mr-2" />
                {vehicleInfo}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-12 text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheck} className="text-3xl text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Appointment Scheduled!
              </h3>
              <p className="text-gray-600">
                We've confirmed your {inquiryType === 'test-drive' ? 'test drive' : 'appointment'} for
              </p>
              <p className="text-lg font-semibold text-primary-600 mt-2">
                {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                <br />
                at {selectedTime}
              </p>
              <p className="text-sm text-gray-500 mt-4">
                A confirmation email has been sent to {formData.email}
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
              {/* Calendar Section */}
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Select Date {selectedDate && (
                      <span className="text-sm font-normal text-primary-600 ml-2">
                        ({selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                      </span>
                    )}
                  </h3>
                  
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={handlePreviousMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      disabled={currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear()}
                    >
                      <SafeIcon icon={FiChevronLeft} />
                    </button>
                    <h4 className="text-lg font-semibold">
                      {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h4>
                    <button
                      onClick={handleNextMonth}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiChevronRight} />
                    </button>
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day headers */}
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                    
                    {/* Empty cells for alignment */}
                    {Array.from({ length: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    
                    {/* Days */}
                    {getDaysInMonth(currentMonth).map(date => {
                      const isPast = isDatePast(date);
                      const isAvailable = isDateAvailable(date);
                      const isSelected = selectedDate?.toDateString() === date.toDateString();
                      const isToday = date.toDateString() === new Date().toDateString();
                      
                      return (
                        <button
                          key={date.toISOString()}
                          onClick={() => !isPast && isAvailable && handleDateSelect(date)}
                          disabled={isPast || !isAvailable}
                          className={`
                            p-2 rounded-lg text-sm font-medium transition-all
                            ${isPast ? 'text-gray-300 cursor-not-allowed' : ''}
                            ${!isPast && !isAvailable ? 'text-gray-400 cursor-not-allowed line-through' : ''}
                            ${!isPast && isAvailable && !isSelected ? 'hover:bg-primary-50 text-gray-700' : ''}
                            ${isSelected ? 'bg-primary-600 text-white shadow-lg' : ''}
                            ${isToday && !isSelected ? 'ring-2 ring-primary-400 font-bold' : ''}
                          `}
                        >
                          {date.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Slots */}
                {selectedDate ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Select Time</h3>
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots[getDateKey(selectedDate)]?.map(time => (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          className={`
                            px-3 py-2 rounded-lg text-sm font-medium transition-all
                            ${selectedTime === time 
                              ? 'bg-primary-600 text-white' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }
                          `}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-amber-800 font-medium">👆 Click a date above to see available time slots</p>
                  </div>
                )}
              </div>

              {/* Form Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Information</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <SafeIcon icon={FiUser} className="inline mr-2" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <SafeIcon icon={FiMail} className="inline mr-2" />
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <SafeIcon icon={FiPhone} className="inline mr-2" />
                      Phone
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="(817) 382-2316"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <SafeIcon icon={FiMessageSquare} className="inline mr-2" />
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Any special requests or questions..."
                    />
                  </div>

                  {/* Selected Date/Time Summary */}
                  {selectedDate && selectedTime && (
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-primary-900">
                        Appointment Summary:
                      </p>
                      <p className="text-primary-700 mt-1">
                        <SafeIcon icon={FiCalendar} className="inline mr-2" />
                        {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                      <p className="text-primary-700">
                        <SafeIcon icon={FiClock} className="inline mr-2" />
                        {selectedTime}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!selectedDate || !selectedTime || loading}
                    className={`
                      w-full py-3 px-4 rounded-lg font-semibold transition-all
                      ${selectedDate && selectedTime && !loading
                        ? 'bg-primary-600 text-white hover:bg-primary-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }
                    `}
                  >
                    {loading ? 'Scheduling...' : 'Confirm Appointment'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CalendarScheduler;
